import React, { useState, useEffect, useRef } from "react";
import {
	Box,
	Text,
	Button,
	Flex,
	HStack,
	VStack,
	Icon,
	Input,
	IconButton,
	Tabs,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Center,
	Spinner,
} from "@chakra-ui/react";
import { FaArrowLeft, FaCheck, FaUndo } from "react-icons/fa";
import ListAnalysts from "components/analyst/ListAnalysts";
import AnalystDataScopeContainer from "containers/analyst/AnalystDataScopeContainer";
import AnalystMemoryFactsComponent from "components/analyst/AnalystMemoryFactsComponent";
import { AnalystProto, DataScopeProto } from "build-ts/analyst";
import AnalystJobsComponent from "components/analyst/AnalystJobsComponent";
import { Header } from "build-ts/metadata";
import { getAnalyst, createAnalyst, updateAnalyst } from "services/AnalystService";
import { listScheduledJobs } from "services/ScheduleServices";
import { updateHeader } from "services/MetadataServices";
import showToast from "hooks/useCustomToast";
import { v4 as uuidv4 } from "uuid";
import { MetadataTypeEnum } from "build-ts/enums";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import lodash from "lodash";
import { useAnalystChat } from "context/AnalystChatContext";
import { IoChatbubble } from "react-icons/io5";
import { MdEdit, MdMemory } from "react-icons/md";
import { BiSolidNetworkChart } from "react-icons/bi";
import { LuWorkflow } from "react-icons/lu";
import { BsRobot } from "react-icons/bs";

const AnalystPage: React.FC = () => {
	// Read URL params first so !!id can seed the loading state
	const navigate = useNavigate();
	const location = useLocation();
	const { id, jobId: routeJobId } = useParams<{ id?: string; jobId?: string }>();

	const [selectedAnalystData, setSelectedAnalystData] = useState<AnalystProto | undefined>(undefined);
	// store original analyst data for discarded changes
	const [originalAnalystData, setOriginalAnalystData] = useState<AnalystProto | undefined>(undefined);
	const [selectedAnalyst, setSelectedAnalyst] = useState<Header | null>(null);
	const [isCreatingNew, setIsCreatingNew] = useState<boolean>(false);
	// Start in loading state immediately if a URL id is present — prevents flash to list
	const [isLoadingAnalystDetails, setIsLoadingAnalystDetails] = useState<boolean>(!!id);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [hasChanges, setHasChanges] = useState<boolean>(false);
	const [hasDiscardedChanges, setHasDiscardedChanges] = useState<boolean>(false);
	// State for job viewing/editing - used to control overlay button visibility
	const [isViewingOrEditingJob, setIsViewingOrEditingJob] = useState<boolean>(false);
	// Track whether attribute available_values need a refresh before saving
	const [attributesNeedRefresh, setAttributesNeedRefresh] = useState<boolean>(false);
	// True while a refresh API call is in-flight
	const [isScopeLocked, setIsScopeLocked] = useState<boolean>(false);
	const refreshAttributeValuesRef = useRef<(() => Promise<void>) | null>(null);
	// Timestamp bumped on every successful save — signals container to clear "New" tab 
	const [analystSavedAt, setAnalystSavedAt] = useState<number>(0);
	// State for analyst jobs map
	const [analystJobsMap, setAnalystJobsMap] = useState<Map<string, Header[]>>(new Map());
	const [activeAnalystsMap, setActiveAnalystsMap] = useState<{
		analystId: string,
		active: boolean,
		noOfActiveJobs: number,
		noOfTotalJobs: number
	}[]>([]);
	const [isLoadingAllJobs, setIsLoadingAllJobs] = useState<boolean>(false);
	const [isLoadingAllAnalysts, setIsLoadingAllAnalysts] = useState<boolean>(false);

	const {
		setAnalystsList, analystsList, isLoadingAnalysts,
		analystProtoMap, setAnalystProtoMap,
	} = useAnalystChat();

	// Use refs to store original individual pieces for change detection
	const originalDataRef = useRef<{
		name: string;
		agentInstructions: string;
		notes: string;
		dsUseDefaultScope: boolean;
		dsMetrics: string[];
		dsBenchmarks: string[];
		dsColumnScopes: Array<{
			id: string | undefined;
			operator: number | undefined;
			values: string[] | undefined;
			is_hidden: boolean | undefined;
			available_values: string[] | undefined;
		}>;
	}>({
		name: "",
		agentInstructions: "",
		notes: "",
		dsUseDefaultScope: true,
		dsMetrics: [],
		dsBenchmarks: [],
		dsColumnScopes: [],
	});

	const [isEditingAnalystName, setIsEditingAnalystName] = useState(false);
	const [analystNameDraft, setAnalystNameDraft] = useState("");

	const handleAnalystNameChange = async () => {
		const next = analystNameDraft.trim() || (isCreatingNew ? "New analyst" : "Untitled analyst");

		setSelectedAnalystData(prev => {
			if (!prev) {
				return {
					header: { name: next },
					role: "",
					instructions: "",
					notes: "",
					data_scope: DataScopeProto.create({}),
				} as AnalystProto;
			}
			return { ...prev, header: { ...prev.header, name: next } };
		});
		setIsEditingAnalystName(false);

		// Persist to the server only for already-saved analysts
		const analystId = selectedAnalystData?.header?.id;
		if (!isCreatingNew && analystId) {
			if (selectedAnalystData?.header?.name === next) {
				return;
			}
			const updatedHeader = await updateHeader(
				analystId,
				next,
				selectedAnalystData?.header?.description ?? "",
				MetadataTypeEnum.ANALYST,
			);
			if (updatedHeader) {
				// Keep selectedAnalystData header in sync with what the server returned
				setSelectedAnalystData(prev =>
					prev ? { ...prev, header: updatedHeader } : prev
				);
				// Update the map cache — only replace the header; preserve the cached data scope
				setAnalystProtoMap(prev => {
					const existing = prev.get(updatedHeader.id!);
					if (!existing) return prev;
					return new Map(prev).set(updatedHeader.id!, { ...existing, header: updatedHeader });
				});
				// Update original analyst data — only update the header
				setOriginalAnalystData(prev =>
					prev ? { ...prev, header: updatedHeader } : prev
				);
				// Reflect the rename in the analyst list
				setAnalystsList(prev =>
					prev.map(h => h.id === updatedHeader.id ? updatedHeader : h)
				);
				// Sync the change-detection ref so the rename is not treated as an unsaved change
				originalDataRef.current = {
					...originalDataRef.current,
					name: updatedHeader.name || "",
				};
			}
		}
	};

	useEffect(() => {
		if (!isEditingAnalystName) {
			setAnalystNameDraft(selectedAnalystData?.header?.name || "");
		}
	}, [selectedAnalystData?.header?.id, selectedAnalystData?.header?.name, isEditingAnalystName, isCreatingNew]);

	// Detect changes by comparing current individual pieces with original
	useEffect(() => {
		if (isCreatingNew) {
			// For new analysts, no change detection needed
			setHasChanges(false);
			return;
		}

		if (!selectedAnalystData || isLoadingAnalystDetails) {
			setHasChanges(false);
			return;
		}

		// Extract current dataScope
		// When data_scope is absent, the container treats it as use_default_scope=true
		const currentDsUseDefaultScope = selectedAnalystData.data_scope
			? (selectedAnalystData.data_scope.use_default_scope || false)
			: true;
		const currentDsMetrics = (selectedAnalystData.data_scope?.metrics || [])
			.map(m => m.header?.id)
			.filter((id): id is string => !!id)
			.sort();
		const currentDsBenchmarks = (selectedAnalystData.data_scope?.benchmarks || [])
			.map(b => b.header?.id)
			.filter((id): id is string => !!id)
			.sort();
		const currentDsColumnScopes = (selectedAnalystData.data_scope?.column_scopes || [])
			.map(cs => ({
				id: cs?.kg_column?.header?.id,
				operator: cs.operator,
				values: cs.values ? [...cs.values].sort() : undefined,
				is_hidden: cs.is_hidden,
				available_values: cs.available_values ? [...cs.available_values].sort() : undefined,
			}));

		// Compare each field - use lodash.isEqual for arrays
		const hasChanged =
			(selectedAnalystData.header?.name || "") !== originalDataRef.current.name ||
			(selectedAnalystData.instructions || "") !== originalDataRef.current.agentInstructions ||
			(selectedAnalystData.notes || "") !== originalDataRef.current.notes;

		// Check if data scope has changed
		const dataScopeChanged =
			currentDsUseDefaultScope !== originalDataRef.current.dsUseDefaultScope ||
			!lodash.isEqual(currentDsMetrics, originalDataRef.current.dsMetrics) ||
			!lodash.isEqual(currentDsBenchmarks, originalDataRef.current.dsBenchmarks) ||
			!lodash.isEqual(currentDsColumnScopes, originalDataRef.current.dsColumnScopes);

		setHasChanges(hasChanged || dataScopeChanged);

	}, [selectedAnalystData, isCreatingNew, isLoadingAnalystDetails]);

	// Fetch jobs for all analysts when analysts are loaded
	useEffect(() => {
		const fetchAllAnalystJobs = async () => {
			if (!isLoadingAnalysts && analystsList.length > 0) {
				setIsLoadingAllJobs(true);
				setActiveAnalystsMap([]); // Reset before fetching
				try {
					// Fetch jobs for all analysts in parallel
					const jobPromises = analystsList.map(async (analyst) => {
						if (analyst.id) {
							const response = await listScheduledJobs(
								"SCENARIO_JOB",
								-1,
								1,
								analyst.id,
								"LAST_MODIFIED",
								false,
								true, /* includeScheduleActivity */
								"", /* activeSchedule */
							);
							return { analystId: analyst.id, jobs: response || [] };
						}
						return { analystId: analyst.id || "", jobs: [] };
					});

					const results = await Promise.all(jobPromises);

					// Build the map
					const newMap = new Map<string, Header[]>();
					results.forEach(({ analystId, jobs }) => {
						if (analystId) {
							newMap.set(analystId, jobs);
						}
					});

					setAnalystJobsMap(newMap);

					// Build active analysts map
					const activeAnalysts: {
						analystId: string;
						active: boolean;
						noOfActiveJobs: number;
						noOfTotalJobs: number;
					}[] = [];

					newMap.forEach((jobs, analystId) => {
						const activeJobs = jobs.filter(job => job.has_active_schedule);
						const totalJobs = jobs.length;
						activeAnalysts.push({
							analystId,
							active: activeJobs.length > 0,
							noOfActiveJobs: activeJobs.length,
							noOfTotalJobs: totalJobs
						});
					});

					setActiveAnalystsMap(activeAnalysts);
				} catch (error) {
					console.error("Error fetching all analyst jobs:", error);
				} finally {
					setIsLoadingAllJobs(false);
				}
			}
		};

		fetchAllAnalystJobs();
	}, [isLoadingAnalysts]);

	// Fetch full AnalystProto details and memory facts for all analysts in parallel
	useEffect(() => {
		if (isLoadingAnalysts || analystsList.length === 0) return;

		const ids = analystsList.filter(a => a.id).map(a => a.id!);

		const uncachedAnalysts = ids.filter(id => !analystProtoMap.has(id));
		if (uncachedAnalysts.length > 0) {
			setIsLoadingAllAnalysts(true);
			Promise.all(uncachedAnalysts.map(id => getAnalyst(id)))
				.then(results => {
					setAnalystProtoMap(prev => {
						const next = new Map(prev);
						uncachedAnalysts.forEach((id, i) => { if (results[i]) next.set(id, results[i]!); });
						return next;
					});
				})
				.catch(err => console.error("Error fetching all analyst details:", err))
				.finally(() => setIsLoadingAllAnalysts(false));
		}

	}, [isLoadingAnalysts]);

	// Load analyst when navigating directly to /analyst/:id
	useEffect(() => {
		const loadAnalyst = async () => {
			if (id && analystsList.length > 0 && !selectedAnalyst) {
				const analystHeader = analystsList.find(a => a.id === id);
				if (analystHeader) {
					setIsLoadingAnalystDetails(true);
					setSelectedAnalyst(analystHeader);
					setIsCreatingNew(false);
					try {
						const cached = analystProtoMap.get(analystHeader.id!);
						const analystData = cached ?? await getAnalyst(analystHeader.id!);
						if (!cached && analystData) {
							setAnalystProtoMap(prev => new Map(prev).set(analystHeader.id!, analystData));
						}
						setSelectedAnalystData(analystData || undefined);
						setOriginalAnalystData(analystData || undefined);
						originalDataRef.current = {
							name: analystData?.header?.name || "",
							agentInstructions: analystData?.instructions || "",
							notes: analystData?.notes || "",
							dsUseDefaultScope: analystData?.data_scope
								? (analystData.data_scope.use_default_scope || false)
								: true,
							dsMetrics: (analystData?.data_scope?.metrics || [])
								.map(m => m.header?.id)
								.filter((id): id is string => !!id)
								.sort(),
							dsBenchmarks: (analystData?.data_scope?.benchmarks || [])
								.map(b => b.header?.id)
								.filter((id): id is string => !!id)
								.sort(),
							dsColumnScopes: (analystData?.data_scope?.column_scopes || [])
								.map(cs => ({
									id: cs?.kg_column?.header?.id,
									operator: cs.operator,
									values: cs.values ? [...cs.values].sort() : undefined,
									is_hidden: cs.is_hidden,
									available_values: cs.available_values ? [...cs.available_values].sort() : undefined,
								})),
						};
					} catch (error) {
						console.error("Error fetching analyst details:", error);
						setSelectedAnalystData(undefined);
						setOriginalAnalystData(undefined);
					} finally {
						setIsLoadingAnalystDetails(false);
					}
				}
			}
		};
		loadAnalyst();
	}, [id, analystsList, selectedAnalyst]);

	// Handle analyst selection for editing
	const handleAnalystSelection = async (analyst: Header) => {
		setIsLoadingAnalystDetails(true);
		setSelectedAnalyst(analyst);
		setIsCreatingNew(false);
		// Update URL to include analyst ID
		if (analyst?.id) {
			navigate(`/analyst/${analyst.id}`, { replace: true });
		}

		// Fetch analyst data (use cache if available)
		try {
			if (analyst?.id) {
				const cached = analystProtoMap.get(analyst.id);
				const analystData = cached ?? await getAnalyst(analyst.id);
				if (!cached && analystData) {
					setAnalystProtoMap(prev => new Map(prev).set(analyst.id!, analystData));
				}
				setSelectedAnalystData(analystData || undefined);
				setOriginalAnalystData(analystData || undefined);

				// Store original data as individual pieces (arrays, not JSON strings)
				originalDataRef.current = {
					name: analystData?.header?.name || "",
					agentInstructions: analystData?.instructions || "",
					notes: analystData?.notes || "",
					dsUseDefaultScope: analystData?.data_scope
						? (analystData.data_scope.use_default_scope || false)
						: true,
					dsMetrics: (analystData?.data_scope?.metrics || [])
						.map(m => m.header?.id)
						.filter((id): id is string => !!id)
						.sort(),
					dsBenchmarks: (analystData?.data_scope?.benchmarks || [])
						.map(b => b.header?.id)
						.filter((id): id is string => !!id)
						.sort(),
					dsColumnScopes: (analystData?.data_scope?.column_scopes || [])
						.map(cs => ({
							id: cs?.kg_column?.header?.id,
							operator: cs.operator,
							values: cs.values ? [...cs.values].sort() : undefined,
							is_hidden: cs.is_hidden,
							available_values: cs.available_values ? [...cs.available_values].sort() : undefined,
						})),
				};
			}
		} catch (error) {
			console.error("Error fetching analyst details:", error);
			setSelectedAnalystData(undefined);
		} finally {
			setIsLoadingAnalystDetails(false);
		}
	};

	// Handle creating a new analyst
	const handleCreateNewAnalyst = () => {
		setSelectedAnalyst(null);
		setSelectedAnalystData(undefined);
		setOriginalAnalystData(undefined);
		setIsCreatingNew(true);
		setHasChanges(false);

		// Reset original data (empty arrays)
		originalDataRef.current = {
			name: "",
			agentInstructions: "",
			notes: "",
			dsUseDefaultScope: true,
			dsMetrics: [],
			dsBenchmarks: [],
			dsColumnScopes: [],
		};

		navigate("/analyst");
	};

	// Handle back to list
	const handleBackToList = () => {
		setSelectedAnalyst(null);
		setSelectedAnalystData(undefined);
		setOriginalAnalystData(undefined);
		setIsCreatingNew(false);
		setHasChanges(false);

		// Reset original data (empty arrays)
		originalDataRef.current = {
			name: "",
			agentInstructions: "",
			notes: "",
			dsUseDefaultScope: true,
			dsMetrics: [],
			dsBenchmarks: [],
			dsColumnScopes: [],
		};

		navigate("/analyst");
	};

	// Handle save analyst (create new or update existing).
	// dataScopeOverride: when called from the constraint-save auto-flow (the container already
	// refreshed attribute values and built an up-to-date scope), pass the scope directly so we
	// avoid reading potentially stale React state from selectedAnalystData.
	const handleSaveAnalyst = async (dataScopeOverride?: DataScopeProto) => {
		const effectiveDataScope = dataScopeOverride ?? selectedAnalystData?.data_scope;

		if (!effectiveDataScope || !effectiveDataScope.metrics.length) {
			showToast("Please select data scope", "", "warning");
			return;
		}

		// Validate required fields
		if (!selectedAnalystData?.header?.name || selectedAnalystData.header.name.trim() === "") {
			showToast("Please enter analyst name", "", "warning");
			return;
		}

		setIsSaving(true);

		// Auto-refresh stale attribute values before persisting (main save button fallback).
		// Skipped when dataScopeOverride is provided because the constraint-save flow already refreshed.
		if (!dataScopeOverride && attributesNeedRefresh && refreshAttributeValuesRef.current) {
			try {
				await refreshAttributeValuesRef.current();
			} catch {
				showToast("Failed to refresh attribute values before saving", "", "error");
				setIsSaving(false);
				return;
			}
		}

		try {
			if (isCreatingNew) {
				// Create new analyst
				const newAnalyst: AnalystProto = {
					...selectedAnalystData,
					header: {
						...selectedAnalystData.header,
						id: uuidv4(),
						type: MetadataTypeEnum.ANALYST,
						created_date_ms: Date.now(),
						last_modified_date_ms: Date.now(),
						last_modified_by: "current_user",
						description: "",
					},
				};

				const result = await createAnalyst(newAnalyst);

				if (result && result.header) {
					showToast("Analyst created successfully", "", "success");
					setAnalystSavedAt(Date.now());

					// Add new analyst to top of list
					setAnalystsList(prev => [result.header!, ...prev]);

					// Select the newly created analyst
					setSelectedAnalyst(result.header);
					setSelectedAnalystData(result);
					setOriginalAnalystData(result);
					setIsCreatingNew(false);

					// Store as new original data (arrays, not JSON strings)
					originalDataRef.current = {
						name: result.header?.name || "",
						agentInstructions: result.instructions || "",
						notes: result.notes || "",
						dsUseDefaultScope: result.data_scope
							? (result.data_scope.use_default_scope || false)
							: true,
						dsMetrics: (result.data_scope?.metrics || [])
							.map(m => m.header?.id)
							.filter((id): id is string => !!id)
							.sort(),
						dsBenchmarks: (result.data_scope?.benchmarks || [])
							.map(b => b.header?.id)
							.filter((id): id is string => !!id)
							.sort(),
						dsColumnScopes: (result.data_scope?.column_scopes || [])
							.map(cs => ({
								id: cs?.kg_column?.header?.id,
								operator: cs.operator,
								values: cs.values ? [...cs.values].sort() : undefined,
								is_hidden: cs.is_hidden,
								available_values: cs.available_values ? [...cs.available_values].sort() : undefined,
							})),
					};
					setHasChanges(false);

					// Update analystProtoMap with newly created analyst
					if (result.header?.id) {
						setAnalystProtoMap(prev => new Map(prev).set(result.header!.id!, result));
						navigate(`/analyst/${result.header.id}`, { replace: true });
					}
				}
			} else {
				// Update existing analyst (use effectiveDataScope to get the freshest scope)
				const updatedAnalyst: AnalystProto = {
					...selectedAnalystData,
					data_scope: effectiveDataScope,
					header: {
						...selectedAnalystData.header,
						last_modified_date_ms: Date.now(),
						last_modified_by: "current_user",
						description: "",
					},
				};

				const result = await updateAnalyst(updatedAnalyst, true);

				if (result && result.header) {
					showToast("Analyst updated successfully", "", "success");
					setAnalystSavedAt(Date.now());

					// Update the analyst in the list
					setAnalystsList(prev =>
						prev.map(h => h.id === result.header!.id ? result.header! : h)
					);

					// Update local state
					setSelectedAnalyst(result.header);
					setSelectedAnalystData(result);
					setOriginalAnalystData(result);

					// Update original data to new saved state (arrays, not JSON strings)
					originalDataRef.current = {
						name: result.header?.name || "",
						agentInstructions: result.instructions || "",
						notes: result.notes || "",
						dsUseDefaultScope: result.data_scope
							? (result.data_scope.use_default_scope || false)
							: true,
						dsMetrics: (result.data_scope?.metrics || [])
							.map(m => m.header?.id)
							.filter((id): id is string => !!id)
							.sort(),
						dsBenchmarks: (result.data_scope?.benchmarks || [])
							.map(b => b.header?.id)
							.filter((id): id is string => !!id)
							.sort(),
						dsColumnScopes: (result.data_scope?.column_scopes || [])
							.map(cs => ({
								id: cs?.kg_column?.header?.id,
								operator: cs.operator,
								values: cs.values ? [...cs.values].sort() : undefined,
								is_hidden: cs.is_hidden,
								available_values: cs.available_values ? [...cs.available_values].sort() : undefined,
							})),
					};
					setHasChanges(false);

					// Update analystProtoMap with updated analyst
					if (result.header?.id) {
						setAnalystProtoMap(prev => new Map(prev).set(result.header!.id!, result));
					}
				}
			}
		} catch (error) {
			showToast("Failed to save analyst", "", "error");
			console.error("Error saving analyst:", error);
		} finally {
			setIsSaving(false);
		}
	};

	// Handle cancel or discard changes - reset to original data
	const handleCancel = () => {
		if (hasChanges && !isCreatingNew && selectedAnalystData) {
			if (originalAnalystData) {
				// Reset selectedAnalystData to original
				setSelectedAnalystData({ ...originalAnalystData });
				setHasDiscardedChanges(true);
				showToast("Changes discarded", "", "info");
			} else {
				handleBackToList();
			}
		} else if (isCreatingNew) {
			// Go back to list for new analyst
			handleBackToList();
		}
	};

	// Handle opening chat in new tab
	const handleOpenChat = () => {
		if (selectedAnalyst?.id) {
			window.open(`/chat?chatAnalystId=${selectedAnalyst.id}`, "_blank");
		}
	};

	const showFooterBar = (isCreatingNew || hasChanges) && !isViewingOrEditingJob;

	const activeTabIndexFromPath = (() => {
		if (isCreatingNew) return 0;
		const path = location.pathname;
		if (path.includes("/memory")) return 1;
		if (path.includes("/jobs")) return 2;
		return 0;
	})();

	const analystViewTabIndex = isLoadingAnalystDetails ? 0 : activeTabIndexFromPath;

	const handleAnalystViewTabChange = (idx: number) => {
		if (isLoadingAnalystDetails) return;
		if (isCreatingNew || !selectedAnalystData?.header?.id) return;
		const base = `/analyst/${selectedAnalystData.header.id}`;
		if (idx === 1) {
			navigate(`${base}/memory`);
		} else if (idx === 2) {
			navigate(`${base}/jobs`);
		} else {
			navigate(base);
		}
	};

	const renderTabs = () => (
		<Flex direction="column" height="100%" bg="white" flex="1" minH={0}>
			<Tabs
				colorScheme="purple"
				variant="line"
				// isLazy
				// lazyBehavior="keepMounted"
				index={analystViewTabIndex}
				onChange={handleAnalystViewTabChange}
				display="flex"
				flexDirection="column"
				flex="1"
				minH={0}
				w="100%"
			>
				{/* Header: back, avatar + editable name, tab list, Start Chat */}
				<HStack
					px={3}
					mt={1}
					pb={0.5}
					borderBottom="1px solid"
					borderColor="gray.200"
					align="flex-end"
					gap={0}
					w="100%"
					spacing={0}
					flexShrink={0}
				>
					<HStack
						spacing={3}
						minW={0}
						pr={1}
						flexShrink={0}
						maxW="min(24rem, 40vw)"
						alignSelf="center"
						align="center"
					>
						<Button
							leftIcon={<Icon as={FaArrowLeft} boxSize={4} />}
							variant="ghost"
							size="sm"
							onClick={handleBackToList}
							color="gray.700"
							_hover={{ bg: "gray.100" }}
							flexShrink={0}
							iconSpacing={0}
							aria-label="Back to analysts"
						/>
						<HStack spacing={3} align="center" minW={0} flex="1" maxW="100%">
							<Icon as={BsRobot} boxSize={7} color="purple.600" />
							{isEditingAnalystName ? (
								<HStack spacing={2} align="center" minW={0} maxW="100%" flex={1}>
									<Input
										variant="flushed"
										size="lg"
										fontWeight="600"
										color="gray.800"
										autoFocus
										placeholder={isCreatingNew ? "Name your analyst" : "Analyst name"}
										value={analystNameDraft}
										onChange={e => setAnalystNameDraft(e.target.value)}
										onBlur={handleAnalystNameChange}
										onKeyDown={e => {
											if (e.key === "Enter") {
												handleAnalystNameChange();
											} else if (e.key === "Escape") {
												setAnalystNameDraft(selectedAnalystData?.header?.name || "");
												setIsEditingAnalystName(false);
											}
										}}
										minW="0"
										flex={1}
										borderColor="purple.400"
										_focusVisible={{ borderColor: "purple.500", boxShadow: "none" }}
									/>
									<IconButton
										aria-label="Save analyst name"
										icon={<Icon as={FaCheck} boxSize={5} color="green.500" />}
										size="xs"
										colorScheme="green"
										variant="ghost"
										flexShrink={0}
										onMouseDown={e => e.preventDefault()}
										onClick={() => handleAnalystNameChange()}
									/>
								</HStack>
							) : (
								<HStack
									role="group"
									spacing={2}
									align="center"
									minW={0}
									maxW="100%"
									cursor="pointer"
									onClick={() => {
										setAnalystNameDraft(selectedAnalystData?.header?.name || "");
										setIsEditingAnalystName(true);
									}}
									_hover={{ opacity: 0.92 }}
									flex="1"
								>
									<Text
										fontSize="md"
										fontWeight="700"
										color="gray.800"
										mb={0}
										textAlign="left"
										display="block"
										noOfLines={1}
										lineHeight="short"
										py={0}
										borderBottomWidth="2px"
										borderColor="transparent"
										_groupHover={{ borderColor: "purple.200" }}
									>
										{selectedAnalystData?.header?.name ||
											(isCreatingNew ? "New analyst" : "Untitled analyst")}
									</Text>
									<Icon
										as={MdEdit}
										boxSize={4}
										color="gray.600"
										opacity={0.6}
										_groupHover={{ opacity: 1 }}
										flexShrink={0}
									/>
								</HStack>
							)}
						</HStack>
					</HStack>
					<Flex flex="1" minW={0} pl={1} pr={0} align="flex-end" justify="flex-end" overflow="visible">
						<TabList w="auto" maxW="100%" display="flex" flexShrink={1} minW={0} gap={0} px={0} borderBottom="none">
							<Tab isDisabled={isLoadingAnalystDetails} fontWeight="600" _selected={{ fontWeight: "700", borderBottomWidth: "3px", borderColor: "purple.700" }}>
								<HStack as="span" spacing={2} whiteSpace="nowrap" justify="flex-start" align="center" flex="1" minW={0}>
									<Icon as={BiSolidNetworkChart} boxSize={6} flexShrink={0} />
									<Text as="span" display="inline" fontSize="md" mb={0} textAlign="left">Scope</Text>
								</HStack>
							</Tab>
							{!isCreatingNew && (
								<Tab isDisabled={isLoadingAnalystDetails} fontWeight="600" _selected={{ fontWeight: "700", borderBottomWidth: "3px", borderColor: "purple.700" }}>
									<HStack as="span" spacing={2} whiteSpace="nowrap" justify="flex-start" align="center" flex="1" minW={0}>
										<Icon as={MdMemory} boxSize={6} flexShrink={0} />
										<Text as="span" display="inline" fontSize="md" mb={0} textAlign="left">Memory</Text>
									</HStack>
								</Tab>
							)}
							{!isCreatingNew && (
								<Tab isDisabled={isLoadingAnalystDetails || !selectedAnalystData?.header?.id} fontWeight="600" _selected={{ fontWeight: "700", borderBottomWidth: "3px", borderColor: "purple.700" }}>
									<HStack as="span" spacing={2} whiteSpace="nowrap" justify="flex-start" align="center" flex="1" minW={0}>
										<Icon as={LuWorkflow} boxSize={6} flexShrink={0} />
										<Text as="span" display="inline" fontSize="md" mb={0} textAlign="left">Workflows</Text>
									</HStack>
								</Tab>
							)}
						</TabList>
					</Flex>
					<Box flexShrink={0} pl={2} minW="min-content" alignSelf="center">
						{selectedAnalyst && !isCreatingNew && (
							<Button
								leftIcon={<Icon as={IoChatbubble} boxSize={4} />}
								onClick={handleOpenChat}
								size="md"
								colorScheme="purple"
								bg="purple.700"
								_hover={{ bg: "purple.600" }}
								color="white"
								fontWeight="600"
								fontSize="md"
								whiteSpace="nowrap"
							>
								Start Chat
							</Button>
						)}
					</Box>
				</HStack>

				{/* Tab panels — directly wired to the Tabs above */}
				<TabPanels flex="1" minH={0} display="flex" flexDirection="column" overflow="auto" w="100%">
					{/* Scope */}
					<TabPanel p={0} pt={3} flex="1" minH={0} display="flex" flexDirection="column" overflow="hidden">
						<VStack align="stretch" spacing={3} flex="1" minH={0} overflow="auto">
							<AnalystDataScopeContainer
								analyst={selectedAnalystData}
								setAnalyst={setSelectedAnalystData}
								isCreatingNew={isCreatingNew}
								hasDiscardedChanges={hasDiscardedChanges}
								setHasDiscardedChanges={setHasDiscardedChanges}
								originalAnalyst={originalAnalystData}
								onAttributesNeedRefreshChange={setAttributesNeedRefresh}
								onScopeLockedChange={setIsScopeLocked}
								refreshAttributeValuesRef={refreshAttributeValuesRef}
								analystSavedAt={analystSavedAt}
								onTriggerAnalystSave={!isCreatingNew ? handleSaveAnalyst : undefined}
							/>
						</VStack>
					</TabPanel>

					{/* Memory */}
					{!isCreatingNew && (
						<TabPanel p={0} flex="1" minH={0} overflowY="auto">
							{selectedAnalystData?.header?.id && (
								<AnalystMemoryFactsComponent analystId={selectedAnalystData.header.id} />
							)}
						</TabPanel>
					)}

					{/* Workflows */}
					{!isCreatingNew && (
						<TabPanel p={0} height="100%" flex="1" minH={0} overflow="hidden" display="flex" flexDirection="column">
							{selectedAnalystData?.header?.id && (
								<AnalystJobsComponent
									analystId={selectedAnalystData.header.id}
									setIsViewingOrEditingJob={setIsViewingOrEditingJob}
									analystJobsMap={analystJobsMap}
									setAnalystJobsMap={setAnalystJobsMap}
									initialJobId={routeJobId}
									analystName={selectedAnalystData.header.name?.trim() || "Untitled Analyst"}
								/>
							)}
						</TabPanel>
					)}
				</TabPanels>
			</Tabs>

			{/* Footer: only when creating or has unsaved changes */}
			{showFooterBar && (
				<Flex
					flexShrink={0}
					borderTop="1px"
					borderColor="gray.200"
					py={2}
					px={6}
					bg="white"
					align="center"
					justify="flex-end"
					gap={3}
				>
					{hasChanges && (
						<Text fontSize="md" color="gray.700" fontWeight="600" mb={0}>
							You have unsaved changes.
						</Text>
					)}
					{!hasChanges && <Box flex="1" />}

					<HStack spacing={3}>
						{(isCreatingNew || hasChanges) && (
							<Button
								colorScheme="green"
								size="md"
								onClick={() => handleSaveAnalyst()}
								isLoading={isSaving}
								isDisabled={isScopeLocked || isSaving}
								loadingText={isCreatingNew ? "Saving..." : "Updating..."}
								leftIcon={<Icon as={FaCheck} boxSize={5} />}
								fontWeight="600"
								transition="all 0.2s"
								fontSize="md"
							>
								{isCreatingNew ? "Save" : "Update"}
							</Button>
						)}
						{(isCreatingNew || hasChanges) && (
							<Button
								colorScheme="red"
								variant="outline"
								size="md"
								onClick={handleCancel}
								isDisabled={isSaving || isScopeLocked}
								leftIcon={<Icon as={FaUndo} boxSize={5} />}
								fontWeight="600"
								fontSize="md"
							>
								{isCreatingNew ? "Cancel" : "Discard"}
							</Button>
						)}
					</HStack>
				</Flex>
			)}
		</Flex>
	);

	const renderMainContent = () => {
		if (isLoadingAnalystDetails) {
			return (
				<Center height="100%">
					<VStack spacing={4}>
						<Spinner size="xl" color="purple.600" thickness="3px" />
						<Text color="gray.600" fontSize="md" mb={0}>Loading. Please hold on...</Text>
					</VStack>
				</Center>
			);
		}

		// Deep-link to a specific job: skip the analyst tabs entirely and let
		// AnalystJobsComponent render its own breadcrumb + workflow chrome.
		if (routeJobId && selectedAnalystData) {
			return (
				<Flex direction="column" height="100%" bg="white" flex="1" minH={0}>
					<AnalystJobsComponent
						analystId={id}
						setIsViewingOrEditingJob={setIsViewingOrEditingJob}
						analystJobsMap={analystJobsMap}
						setAnalystJobsMap={setAnalystJobsMap}
						initialJobId={routeJobId}
						analystName={selectedAnalystData.header?.name?.trim() || "Untitled Analyst"}
					/>
				</Flex>
			);
		}

		if (selectedAnalystData || isCreatingNew) {
			return renderTabs();
		}
		return null;
	};

	return (
		<Box width="100%" height="100vh" bg="gray.50">
			{selectedAnalystData || isCreatingNew || isLoadingAnalystDetails ? (
				<Box width="100%" height="100%">
					{renderMainContent()}
				</Box>
			) : (
				<Box width="100%" height="100%">
					<ListAnalysts
						analystHeaders={analystsList}
						setAnalystHeaders={setAnalystsList}
						selectedAnalyst={selectedAnalyst}
						onAnalystSelection={handleAnalystSelection}
						onCreateNewAnalyst={handleCreateNewAnalyst}
						onBackToList={handleBackToList}
						isLoading={isLoadingAnalysts}
						isLoadingAllJobs={isLoadingAllJobs}
						isLoadingAllAnalysts={isLoadingAllAnalysts}
						activeAnalystsMap={activeAnalystsMap}
						analystProtoMap={analystProtoMap}
					/>
				</Box>
			)}
		</Box>
	);
};

export default AnalystPage;